<template>
  <div class="step4-confirm">
    <!-- 확인 정보 모달 -->
    <div class="confirm-modal">
      <div class="modal-header">
        <h3>{{ $t("workorder_modal_confirm_title") }}</h3>
        <button class="close-btn" @click="prevStep">
          <img src="@/assets/img/step_4_close.svg" alt="" srcset="" />
        </button>
      </div>

      <div class="modal-body">
        <!-- 병원 정보 -->
        <div class="info-section" data-tour="s4-review">
          <div class="info-row">
            <label>{{ $t("workorder_label_hospital") }}<card-field-hint field="hospitalName" /></label>
            <span style="flex: none; margin-right: 10px">{{
              getHospitalName()
            }}</span>
            <span class="sub-text">{{ getHospitalPhone() }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_patient") }}<card-field-hint field="patientName" /></label>
            <span>{{ customerData?.name || "-" }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_phone") }}</label>
            <span>{{ formatPhone(customerData?.mobile) || "-" }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_chart_no") }}<card-field-hint field="orderNo" /></label>
            <span>{{ customerData?.chartNumber || "-" }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_form_surgeon") }}<card-field-hint field="doctorName" /></label>
            <span>{{ customerData?.doctorName || "-" }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_birthdate") }}</label>
            <span>{{ formatDate(customerData?.birthDate) || "-" }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_surgery_date") }}</label>
            <span>{{ formatDate(customerData?.surgeryDate) || "-" }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_work_date") }}</label>
            <span>{{ customerData?.worker?.workDate || "-" }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_delivery_date") }}<card-field-hint field="date" /></label>
            <span>{{
              customerData?.worker?.prostheticDeliveryDate || "-"
            }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_surgery_name") }}</label>
            <span>{{ $t("workorder_label_prosthetic") }}</span>
          </div>
          <div class="info-row">
            <label>{{ $t("workorder_label_surface") }}</label>
            <span>{{ getSurfaceProcessText() }}</span>
          </div>
        </div>

        <!-- 치아그룹별 카드 리뷰 — Wave2 REQ-ADM-MULTI-006(Set+join 평탄화 제거, 카드별 상세 표시) -->
        <div
          v-if="prostheticData?.toothDetails?.length"
          class="card-groups-section"
          data-tour="s4-review-groups"
        >
          <div
            v-for="(detail, index) in prostheticData.toothDetails"
            :key="`group-${index}`"
            class="card-group"
          >
            <div class="card-group-header">
              {{
                $t("workorder_review_group_header", {
                  teeth: sortedTeethText(detail.toothNumbers),
                  treatment: getTreatmentTypeLabel(detail.treatmentType),
                  count: (detail.products || []).length,
                })
              }}
              <card-field-hint field="toothPosition" />
            </div>
            <div
              v-for="(product, pIndex) in detail.products || []"
              :key="`group-${index}-product-${pIndex}`"
              class="card-group-row"
            >
              <!-- No.(카드 시리얼)는 가장 중요한 값이라 최상단 단독 라인, 모노스페이스로 강조 -->
              <div class="card-group-row__no">No.{{ product.randomNo || "-" }}</div>
              <!-- GTIN/LOT는 라벨-값 쌍으로 구조화(문자열 붙여쓰기 방지) -->
              <div class="card-group-row__meta">
                <div class="card-group-field">
                  <span class="card-group-field__label">{{
                    $t("workorder_label_gtin")
                  }}</span>
                  <span class="card-group-field__value">{{
                    product.gtinCode || "-"
                  }}</span>
                </div>
                <div class="card-group-field">
                  <span class="card-group-field__label">{{
                    $t("workorder_label_lot_no")
                  }}</span>
                  <span class="card-group-field__value">{{
                    product.lotNumber || "-"
                  }}</span>
                </div>
              </div>
              <!-- 제품명은 길어질 수 있어 자체 줄에서 줄바꿈 허용 -->
              <div class="card-group-row__product">{{ product.itemName || "-" }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="cancel-btn" @click="prevStep">
          {{ $t("workorder_btn_cancel") }}
        </button>
        <button class="confirm-btn" @click="complete" data-tour="s4-submit">
          {{ $t("workorder_btn_confirm") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CardFieldHint from "./CardFieldHint.vue";
import { getTreatmentTypeLabel } from "@/utils/workOrderLabels";

export default {
  name: "Step4",
  components: { CardFieldHint },
  props: {
    clientData: {
      type: Object,
      default: () => ({}),
    },
    customerData: {
      type: Object,
      default: () => ({}),
    },
    prostheticData: {
      type: Object,
      default: () => ({}),
    },
    prevStep: {
      type: Function,
      default: () => {},
    },
    complete: {
      type: Function,
      default: () => {},
    },
  },
  methods: {
    formatPhone(value) {
      if (!value) return "";
      return value;
    },

    formatDate(value) {
      if (!value) return "";
      // YYYY-MM-DD 또는 YYYY/MM/DD 형식으로 반환
      if (typeof value === "string") {
        return value;
      }
      // Date 객체인 경우
      if (value instanceof Date) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const day = String(value.getDate()).padStart(2, "0");
        return `${year}/${month}/${day}`;
      }
      return value;
    },

    // Wave2 REQ-ADM-MULTI-006 — 치아그룹 헤더용 치아번호 오름차순 표시(그룹 단위, 평탄화 아님)
    sortedTeethText(numbers) {
      if (!numbers || numbers.length === 0) return "-";
      return [...numbers].sort((a, b) => a - b).join(", ");
    },

    // SPEC-CHINA-GEO-EDITOR-FIELD-UX-001: 매핑을 utils/workOrderLabels.js로 이관 —
    // ManualWorkOrders.vue·DealerDashboard.vue와 어휘를 공유한다(단일 소유자).
    // this.$t와 i18n.global.t는 동일 인스턴스를 참조하므로 동작은 기존과 동일하다.
    getTreatmentTypeLabel(treatmentType) {
      return getTreatmentTypeLabel(treatmentType);
    },

    getSurfaceProcessText() {
      // Step3에서는 toothDetails에 stm 정보가 있음
      if (
        this.prostheticData?.toothDetails &&
        this.prostheticData.toothDetails.length > 0
      ) {
        const stmTeeth = this.prostheticData.toothDetails.filter(
          (tooth) => tooth.stm === true
        );

        if (stmTeeth.length > 0) {
          return this.$t("workorder_stm_format", { count: stmTeeth.length });
        }
      }
      return this.$t("workorder_label_none");
    },

    getHospitalName() {
      if (this.clientData?.name) {
        const hospitalName = this.clientData.name;
        return `${hospitalName}`;
      }
      return this.$t('workorder_hospital_name_placeholder');
    },

    getHospitalPhone() {
      return this.clientData?.call ? `(${this.clientData.call})` : "";
    },

    formatBusinessNumber(value) {
      if (!value) return "";
      const cleaned = value.replace(/\D/g, "");
      if (cleaned.length === 10) {
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(
          5
        )}`;
      }
      return value;
    },

    // 부모 컴포넌트에서 호출할 검증 메서드
    async validateForm() {
      return true; // Step4는 확인 단계이므로 항상 true
    },

    // 부모 컴포넌트에서 호출할 데이터 가져오기 메서드
    getFormData() {
      return {
        confirmed: true,
        timestamp: new Date().toISOString(),
      };
    },
  },
};
</script>

<style scoped lang="scss">
$Gray-5: #f7f8f8;
$Gray-10: #f1f2f3;
$Gray-20: #e8ebed;
$Gray-30: #dde0e4;
$Gray-50: #acb2b9;
$Gray-60: #8d939a;
$Gray-70: #6e747c;
$Gray-80: #4b5158;
$Gray-90: #2f3337;
$Gray-100: #17191c;
$Blue: #278aec;
$Yellow: #ffe414;

.step4-confirm {
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;

  .confirm-modal {
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    overflow: hidden;

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0px 14px;
      border-bottom: 1px solid #0000001a !important;
      background: white;
      height: 52px;

      h3 {
        overflow: hidden;
        color: var(--Gray-90, #2f3337);
        text-overflow: ellipsis;
        font-family: "SUIT Variable";
        font-size: 17px;
        font-style: normal;
        font-weight: 700;
        line-height: 100%; /* 17px */
        margin: 0;
      }

      .close-btn {
        width: 32px;
        height: 32px;
        border: none;
        background: transparent;
        color: $Gray-60;
        cursor: pointer;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background: $Gray-10;
          color: $Gray-90;
        }

        i {
          font-size: 16px;
        }
      }
    }

    .modal-body {
      padding: 24px;

      .info-section {
        .info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;

          &:last-child {
            border-bottom: none;
          }

          label {
            font-family: "SUIT Variable";
            font-size: 15px;
            font-style: normal;
            font-weight: 500;
            line-height: 24px; /* 160% */
            // 80px→120px: 카드 힌트 아이콘(#hint) 부착 후 "假体交付日期" 등 6자 라벨이
            // 줄바꿈 없이 한 줄에 아이콘까지 들어가도록 넓힘(로컬 시뮬레이션으로 확인).
            // 모든 행에 동일 적용이라 힌트 유무와 무관하게 값 칸 시작 x좌표는 그대로 정렬된다.
            flex: 0 0 120px;
            width: 130px;
            margin-right: 10px;
            margin-bottom: 0;
          }

          span {
            color: var(--Gray-90, #2f3337);
            font-family: "SUIT Variable";
            font-size: 15px;
            font-style: normal;
            font-weight: 400;
            line-height: 24px; /* 160% */
            text-align: left; // 왼쪽 정렬로 변경
            flex: 1;

            &.sub-text {
              color: var(--Gray-60, #8d939a);
              font-family: "SUIT Variable";
              font-size: 15px;
              font-style: normal;
              font-weight: 400;
              line-height: 24px;
            }
          }
        }
      }

      // 치아그룹별 카드 리뷰 (Wave2 REQ-ADM-MULTI-006)
      // .card-groups-section은 템플릿에서 .info-section과 형제 요소이므로
      // .info-section 안이 아닌 .modal-body 바로 아래에 위치해야 스타일이 실제 적용됨
      // (기존 버그: .info-section 내부 중첩 → 셀렉터 불일치로 미적용, 카드 리스트가 텍스트로 뭉쳐 보임)
      .card-groups-section {
        margin-top: 16px;
        display: flex;
        flex-direction: column;
        gap: 12px;

        .card-group {
          border: 1px solid $Gray-20;
          border-radius: 8px;
          overflow: hidden;
        }

        .card-group-header {
          font-family: "SUIT Variable";
          font-size: 14px;
          font-weight: 600;
          color: $Gray-90;
          padding: 10px 14px;
          background: $Gray-5;
          border-bottom: 1px solid $Gray-20;
        }

        .card-group-row {
          padding: 10px 14px;
          font-family: "SUIT Variable";

          &:not(:last-child) {
            border-bottom: 1px dashed $Gray-20;
          }

          &__no {
            font-family: "SF Mono", Monaco, "Consolas", monospace;
            font-size: 13px;
            font-weight: 600;
            color: $Gray-90;
            margin-bottom: 6px;
          }

          &__meta {
            display: flex;
            flex-wrap: wrap;
            gap: 4px 16px;
            margin-bottom: 4px;
          }

          &__product {
            font-size: 13px;
            color: $Gray-90;
            word-break: break-word;
          }
        }

        .card-group-field {
          display: flex;
          align-items: baseline;
          gap: 6px;

          &__label {
            font-size: 12px;
            color: $Gray-60;
            flex: none;
          }

          &__value {
            font-family: "SF Mono", Monaco, "Consolas", monospace;
            font-size: 13px;
            color: $Gray-80;
            word-break: break-all;
          }
        }
      }
    }

    .modal-footer {
      display: flex;
      justify-content: flex-end; // 오른쪽 정렬
      gap: 10px;
      padding: 12px 20px;
      border-top: 1px solid rgba(0, 0, 0, 0.1) !important;
      flex: none;
      .cancel-btn {
        // 확인 버튼과 동일 박스모델로 정렬 — height 부재로 콘텐츠 높이(~15px)까지
        // 쪼그라들어 확인 버튼(40px)과 어긋나던 깨짐 수정. border 없이 hover는 배경 변경으로.
        height: 40px;
        padding: 0 20px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        color: var(--Gray-70, #6e747c);
        text-align: center;
        text-overflow: ellipsis;
        font-family: "SUIT Variable";
        font-size: 15px;
        font-style: normal;
        font-weight: 600;
        line-height: 100%; /* 15px */
        border: none;
        border-radius: 8px;
        background: var(--Gray-20, #e8ebed);
        cursor: pointer;

        &:hover {
          background: var(--Gray-30, #dde0e4);
          color: $Gray-90;
        }
      }

      .confirm-btn {
        width: 120px;
        flex: none;
        height: 40px;
        overflow: hidden;
        color: var(--Gray-100, #17191c);
        text-align: center;
        text-overflow: ellipsis;
        font-family: "SUIT Variable";
        font-size: 15px;
        font-style: normal;
        font-weight: 600;
        line-height: 100%; /* 15px */
        border-radius: 8px;
        background: var(--Yellow, #ffe414);

        &:hover {
          background: #ffc700;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .step4-confirm {
    padding: 16px;

    .confirm-modal {
      .modal-header,
      .modal-body,
      .modal-footer {
        padding: 16px;
      }

      .modal-body {
        .info-section {
          .info-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;

            label {
              flex: none;
            }

            span {
              text-align: left;
            }
          }
        }

        // 카드 그룹 상품 행: 모바일에서는 GTIN/LOT를 세로로 쌓아
        // 긴 값(LOT 등)이 잘리지 않고 자연스럽게 줄바꿈되도록 함
        .card-groups-section {
          .card-group-row {
            &__meta {
              flex-direction: column;
              gap: 4px;
            }
          }

          .card-group-field {
            &__value {
              word-break: break-all;
            }
          }
        }
      }

      .modal-footer {
        flex-direction: column;

        .cancel-btn,
        .confirm-btn {
          width: 100%;
        }
      }
    }
  }
}
</style>
